import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthState } from "./auth";

export interface TaskState {
  id: string;
  created_by: string;
  last_updated_by: string;
  name: { [key: string]: string };
  description: { [key: string]: string };
  category: [string, string];
  frequency_choice: [string, string];
  frequency_value: number;
  start_date: {
    string: string;
  };
  start_time: string;
  target: [string, string];
  rooms: string[];
  status: [string, string];
  time_until_period_end: {
    string: string;
  };
  last_logged: string | null;
  completion_status: {
    completed: boolean;
    outstanding_rooms: string[];
    task_rooms: string[];
  };
}

export interface TaskLogState {
  id: string;
  datetime: string;
  task: [string, string];
  task_category: [string, string];
  profile: string;
  room: [string, string];
  status: [string, string];
}

// Define a type for the slice state
export interface TasksState {
  tasks: {
    data: TaskState[] | null;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  logs: {
    data: TaskLogState[] | null;
    pagination: {
      has_previous: false;
      has_next: false;
      current_page: 1;
      total_pages: 1;
      total_objects: 0;
    };
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  options: {
    [key: string]: any;
  };
}

// Define the initial state using that type
const initialState: TasksState = {
  tasks: {
    data: null,
    loading: false,
    waiting: false,
    errors: [],
  },
  logs: {
    data: null,
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0,
    },
    loading: false,
    waiting: false,
    errors: [],
  },
  options: {},
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get tasks
export const getTasks = createAsyncThunk(
  "tasks/get",
  async (
    obj: {
      category: string | null;
      task: string | null;
      dateRange: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      if (obj.dateRange !== null) params.dateRange = obj.dateRange;
      if (obj.category !== null) params.category = obj.category;
      if (obj.task !== null) params.task = obj.task;
      const searchParams = new URLSearchParams(params);
      const url = `/api/tasks/?${searchParams.toString()}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create task
export const createTask = createAsyncThunk(
  "tasks/post",
  async (
    obj: {
      name: { [key: string]: string };
      description: { [key: string]: string };
      property: string;
      category: string;
      frequencyChoice: string;
      frequencyValue: number;
      startDate: string;
      startTime: string;
      target: string;
      rooms: string[];
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        name: obj.name,
        description: obj.description,
        property: obj.property,
        category: obj.category,
        frequency_choice: obj.frequencyChoice,
        frequency_value: obj.frequencyValue,
        start_date: obj.startDate,
        start_time: obj.startTime,
        target: obj.target,
        rooms: obj.rooms,
        status: obj.status,
      };

      // Build URL
      const url = "/api/tasks/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update task
export const updateTask = createAsyncThunk(
  "tasks/put",
  async (
    obj: {
      taskId: string;
      name: { [key: string]: string };
      description: { [key: string]: string };
      property: string;
      category: string;
      frequencyChoice: string;
      frequencyValue: number;
      startDate: string;
      startTime: string;
      target: string;
      rooms: string[];
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.taskId,
        name: obj.name,
        description: obj.description,
        property: obj.property,
        category: obj.category,
        frequency_choice: obj.frequencyChoice,
        frequency_value: obj.frequencyValue,
        start_date: obj.startDate,
        start_time: obj.startTime,
        target: obj.target,
        rooms: obj.rooms,
        status: obj.status,
      };
      const res = await axios.put("/api/tasks/", body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete photo albums
export const deleteTasks = createAsyncThunk(
  "tasks/delete",
  async (
    obj: {
      taskIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.taskIds },
      };

      const res = await axios.delete("/api/tasks/", config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get task logs
export const getTaskLogs = createAsyncThunk(
  "tasks/logs/get",
  async (
    obj: {
      dateRange: string | null;
      category: string | null;
      task: string | null;
      pageNum: number | boolean;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      const params: { [key: string]: string } = {
        page: obj.pageNum.toString(),
      };
      if (obj.dateRange !== null) params.dateRange = obj.dateRange;
      if (obj.category !== null) params.category = obj.category;
      if (obj.task !== null) params.task = obj.task;
      const searchParams = new URLSearchParams(params);
      const url = `/api/tasks/logs/?${searchParams.toString()}`;

      // Send request
      const res = await axios.get(url.toString(), config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create task log
export const createTaskLog = createAsyncThunk(
  "tasks/logs/post",
  async (
    obj: {
      property: string;
      task: string;
      profile: string;
      room: string;
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        property: obj.property,
        task: obj.task,
        profile: obj.profile,
        room: obj.room,
        status: obj.status,
      };

      // Build URL
      const url = "/api/tasks/logs/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update task log
export const updateTaskLog = createAsyncThunk(
  "tasks/logs/put",
  async (
    obj: {
      taskLogId: string;
      property: string;
      task: string;
      profile: string;
      room: string;
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.taskLogId,
        property: obj.property,
        task: obj.task,
        profile: obj.profile,
        room: obj.room,
        status: obj.status,
      };

      // Build URL
      const url = "/api/tasks/logs/";

      // Send request
      const res = await axios.put(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete task log
export const deleteTaskLogs = createAsyncThunk(
  "tasks/logs/delete",
  async (
    obj: {
      taskLogIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.taskLogIds },
      };

      const res = await axios.delete("/api/tasks/logs/", config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const tasksSlice = createSlice({
  name: "tasks",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Get tasks
      .addCase(getTasks.pending, (state) => {
        state.tasks.loading = true;
        state.tasks.waiting = false;
      })
      .addCase(getTasks.fulfilled, (state, action) => {
        state.tasks.data = action.payload.data.data;
        state.options = action.payload.data.options;
        state.tasks.loading = false;
        state.tasks.waiting = false;
      })
      .addCase(getTasks.rejected, (state) => {
        state.tasks.loading = false;
        state.tasks.waiting = false;
      })

      // Create task
      .addCase(createTask.pending, (state) => {
        state.tasks.loading = true;
        state.tasks.waiting = false;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.tasks.data =
          state.tasks.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.tasks.data];
        state.tasks.loading = false;
        state.tasks.waiting = false;
      })
      .addCase(createTask.rejected, (state) => {
        state.tasks.loading = false;
        state.tasks.waiting = false;
      })

      // Update task
      .addCase(updateTask.pending, (state) => {
        state.tasks.waiting = true;
        state.tasks.loading = true;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        const previousData = state.tasks.data === null ? [] : state.tasks.data;
        const newData = [];
        for (let i = 0; i < previousData.length; i++) {
          const obj = previousData[i];
          if (obj.id === action.payload.data.id) {
            newData.push(action.payload.data);
          } else {
            newData.push(obj);
          }
        }
        state.tasks.data = newData;
        state.tasks.loading = false;
        state.tasks.waiting = false;
      })
      .addCase(updateTask.rejected, (state) => {
        state.tasks.waiting = false;
        state.tasks.loading = false;
      })

      // Delete tasks
      .addCase(deleteTasks.pending, (state) => {
        state.tasks.loading = true;
        state.tasks.waiting = false;
      })
      .addCase(deleteTasks.fulfilled, (state, action) => {
        const existingData = state.tasks.data === null ? [] : state.tasks.data;
        const updatedData = existingData.filter(
          (obj) => !action.payload.data.id.includes(obj.id),
        );
        state.tasks.data = updatedData;
        state.tasks.loading = false;
        state.tasks.waiting = false;
      })
      .addCase(deleteTasks.rejected, (state) => {
        state.tasks.loading = false;
        state.tasks.waiting = false;
      })

      // Get task log
      .addCase(getTaskLogs.pending, (state) => {
        state.logs.loading = true;
        state.logs.waiting = false;
      })
      .addCase(getTaskLogs.fulfilled, (state, action) => {
        state.logs.data = action.payload.data.data;
        state.options = action.payload.data.options;
        state.logs.pagination = action.payload.data.pagination;
        state.logs.loading = false;
        state.logs.waiting = false;
      })
      .addCase(getTaskLogs.rejected, (state) => {
        state.logs.loading = false;
        state.logs.waiting = false;
      })

      // Create task log
      .addCase(createTaskLog.pending, (state) => {
        state.logs.loading = true;
        state.logs.waiting = false;
      })
      .addCase(createTaskLog.fulfilled, (state, action) => {
        state.logs.data =
          state.logs.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.logs.data];
        state.logs.loading = false;
        state.logs.waiting = false;
      })
      .addCase(createTaskLog.rejected, (state) => {
        state.logs.loading = false;
        state.logs.waiting = false;
      })

      // Update task log
      .addCase(updateTaskLog.pending, (state) => {
        state.logs.waiting = true;
        state.logs.loading = true;
      })
      .addCase(updateTaskLog.fulfilled, (state, action) => {
        const previousData = state.logs.data === null ? [] : state.logs.data;
        const newData = [];
        for (let i = 0; i < previousData.length; i++) {
          const obj = previousData[i];
          if (obj.id === action.payload.data.id) {
            newData.push(action.payload.data);
          } else {
            newData.push(obj);
          }
        }
        state.logs.data = newData;
        state.logs.loading = false;
        state.logs.waiting = false;
      })
      .addCase(updateTaskLog.rejected, (state) => {
        state.logs.waiting = false;
        state.logs.loading = false;
      })

      // Delete task logs
      .addCase(deleteTaskLogs.pending, (state) => {
        state.logs.loading = true;
        state.logs.waiting = false;
      })
      .addCase(deleteTaskLogs.fulfilled, (state, action) => {
        const existingData = state.logs.data === null ? [] : state.logs.data;
        const updatedData = existingData.filter(
          (obj) => !action.payload.data.id.includes(obj.id),
        );
        state.logs.data = updatedData;
        state.logs.loading = false;
        state.logs.waiting = false;
      })
      .addCase(deleteTaskLogs.rejected, (state) => {
        state.logs.loading = false;
        state.logs.waiting = false;
      });
  },
});

export default tasksSlice.reducer;
